import { z } from 'zod';

import { serializeUnitConvert } from '#/modules/imsMgmt/settings/mtr/helper/unitAggregateSerialize';

const mtrSpecEntrySchema = z.object({
  mtrSpecId: z.string(),
  specType: z.enum(['SELECT', 'INPUT']),
  required: z.boolean(),
  code: z.string().optional().or(z.literal('')),
  name: z.string().optional().or(z.literal('')),
  label: z.string().optional(),
  mtrSpecOptionId: z.string().optional().or(z.literal('')),
  inputValue: z.string().optional().or(z.literal('')),
});

const mtrSpecCustomSchema = z.object({
  customKey: z.string().max(100, '自訂鍵最多 100 字').optional().or(z.literal('')),
  inputValue: z.string().max(500, '自訂值最多 500 字').optional().or(z.literal('')),
});

const mtrAssemblyOptionSchema = z.object({
  optionMtrId: z.string().min(1),
  mtrNo: z.string().optional().or(z.literal('')),
  name: z.string().optional().or(z.literal('')),
  status: z.string().optional().or(z.literal('')),
});

const mtrUnitConvertSchema = z.object({
  id: z.string().optional().or(z.literal('')),
  code: z.string().max(100, '單位英文縮寫最多 100 字').optional().or(z.literal('')),
  name: z.string().max(200, '單位名稱最多 200 字').optional().or(z.literal('')),
  convertRate: z.union([z.string(), z.number()]).optional().or(z.literal('')),
});

export const mtrFormSchema = z.object({
  mtrKind: z.enum(['NORMAL', 'GROUP']),
  mtrStructMainId: z.string().min(1, '請選擇主分類'),
  mtrStructSubId: z.string().min(1, '請選擇次分類'),
  mtrTemplateId: z.string().optional().or(z.literal('')),
  manufacturerId: z.string().optional().or(z.literal('')),
  /** Unit Aggregate：unit.base（既有列帶 unitBaseId 原樣帶回） */
  unitBaseId: z.string().optional().or(z.literal('')),
  baseUnitCode: z.string().max(100, '基本單位英文縮寫最多 100 字').optional().or(z.literal('')),
  baseUnitName: z.string().max(200, '基本單位名稱最多 200 字').optional().or(z.literal('')),
  /** 最多一筆進位單位；送 API 時轉單一 object｜null */
  unitConvert: z.array(mtrUnitConvertSchema).max(1, '最多一個進位單位'),
  mtrNo: z.string().max(100, '料號最多 100 字').optional().or(z.literal('')),
  name: z.string().trim().min(1, '名稱不能為空').max(200, '名稱最多 200 字'),
  /** 選填排序鍵（後端 trim 後最多 100 字，自然排序） */
  sortKey: z.string().trim().max(100, '排序鍵最多 100 字').optional().or(z.literal('')),
  remark: z.string().max(500, '備註最多 500 字').optional().or(z.literal('')),
  safetyQty: z.union([z.string(), z.number()]).optional().or(z.literal('')),
  /** 可空；有值須 ≥ 0（對齊 BE `base_unit_price`） */
  baseUnitPrice: z.union([z.string(), z.number()]).optional().or(z.literal('')),
  /** 入庫預設數量（非負整數；省略後端存 0） */
  receiptDefaultQty: z.union([z.string(), z.number()]).optional().or(z.literal('')),
  /** NORMAL：BASE｜CONVERT；GROUP：空／null */
  defaultDisplayUnit: z.enum(['BASE', 'CONVERT']).optional().nullable().or(z.literal('')),
  originalSerialListText: z.string().max(2000, '原廠料號過長').optional().or(z.literal('')),
  specEntries: z.array(mtrSpecEntrySchema),
  specCustom: z.array(mtrSpecCustomSchema),
  assemblyOptions: z.array(mtrAssemblyOptionSchema),
  bomId: z.string().optional().or(z.literal('')),
  bomCode: z.string().optional().or(z.literal('')),
  bomName: z.string().optional().or(z.literal('')),
  bomVersion: z.string().optional().or(z.literal('')),
}).superRefine((data, ctx) => {
  if (data.mtrKind === 'NORMAL' && !data.manufacturerId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['manufacturerId'],
      message: '請選擇製造商',
    });
  }

  if (data.baseUnitPrice !== '' && data.baseUnitPrice != null) {
    const price = Number(data.baseUnitPrice);
    if (!Number.isFinite(price) || price < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['baseUnitPrice'],
        message: '基礎單價不可為負數',
      });
    }
  }

  if (data.mtrKind === 'NORMAL'
    && data.receiptDefaultQty !== ''
    && data.receiptDefaultQty != null) {
    const qty = Number(data.receiptDefaultQty);
    if (!Number.isInteger(qty) || qty < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['receiptDefaultQty'],
        message: '入庫預設數量須為非負整數',
      });
    }
  }

  (data.unitConvert ?? []).forEach((row, index) => {
    const name = String(row.name ?? '').trim();
    const rateRaw = row.convertRate;
    const empty = !name && (rateRaw === '' || rateRaw == null);
    if (empty) {
      return;
    }
    // code 已取消必填；有值才保存
    if (!name) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['unitConvert', index, 'name'],
        message: '請填寫名稱',
      });
    }
    const rate = Number(rateRaw);
    if (!Number.isFinite(rate) || rate <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['unitConvert', index, 'convertRate'],
        message: '換算須為大於 0 的數字',
      });
    }
  });

  if (data.mtrKind === 'NORMAL' && data.defaultDisplayUnit === 'CONVERT') {
    const convert = serializeUnitConvert(data.unitConvert);
    if (!convert) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['defaultDisplayUnit'],
        message: '選擇進位單位前請先設定進位單位',
      });
    }
  }
});

export const MTR_FORM_DEFAULT_VALUES = {
  mtrKind: 'NORMAL',
  mtrStructMainId: '',
  mtrStructSubId: '',
  mtrTemplateId: '',
  manufacturerId: '',
  unitBaseId: '',
  baseUnitCode: '',
  baseUnitName: '',
  unitConvert: [],
  mtrNo: '',
  name: '',
  sortKey: '',
  remark: '',
  safetyQty: '',
  baseUnitPrice: '',
  receiptDefaultQty: '',
  defaultDisplayUnit: 'BASE',
  originalSerialListText: '',
  specEntries: [],
  specCustom: [],
  assemblyOptions: [],
  bomId: '',
  bomCode: '',
  bomName: '',
  bomVersion: '',
};

export const EMPTY_UNIT_CONVERT_ROW = {
  id: '',
  code: '',
  name: '',
  convertRate: '1',
};

export const DEFAULT_DISPLAY_UNIT_OPTIONS = [
  { value: 'BASE', label: '基礎單位' },
  { value: 'CONVERT', label: '進位單位' },
];
